import React, { useState, useEffect, useRef } from 'react';
import { applyLicenseKey } from '../services/LicenseService';

// Square Web Payments SDK types
declare global {
  interface Window {
    Square?: {
      payments: (appId: string, locationId: string) => Promise<Payments>;
    };
  }
}

interface Payments {
  card: () => Promise<Card>;
}

interface Card {
  attach: (selector: string) => Promise<void>;
  tokenize: () => Promise<TokenResult>;
  destroy: () => Promise<void>;
}

interface TokenResult {
  status: 'OK' | 'ERROR';
  token?: string;
  errors?: Array<{ message: string }>;
}

interface TrialRegistrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (licenseKey: string) => void;
  licensingServerUrl: string;
  onEnterExistingKey?: () => void;  // Callback to switch to License Management dialog
}

interface SquareConfig {
  applicationId: string | null;
  locationId: string | null;
  sandbox: boolean;
}

interface FormData {
  email: string;
  companyName: string;
  contactName: string;
  password: string;
  confirmPassword: string;
  selectedTier: string;
  useCase: string;
  consentToTerms: boolean;
}

interface TierInfo {
  value: string;
  label: string;
  /** Rate as the server formats it, e.g. "$499/month". Correct for "charged after trial". */
  price: string;
  /**
   * The same figure in cents, kept so the authorization sentence can state a
   * bare amount. The card is authorized ONCE for this much — writing
   * "authorized for $499/month" implies a recurring authorization, which is how
   * a one-time hold ends up looking like a subscription on a bank statement.
   */
  priceCents: number;
}

/** "$499" for whole dollars, "$499.50" when there are cents. */
const formatUsd = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

// Single-Trial model: the trial is of the Professional tier only.
// Fallback used if the Licensing Server tier API is unavailable.
const FALLBACK_TIERS: TierInfo[] = [
  { value: 'PROFESSIONAL', label: 'Professional', price: '', priceCents: 0 },
];

const TrialRegistrationDialog: React.FC<TrialRegistrationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  licensingServerUrl,
  onEnterExistingKey,
}) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    companyName: '',
    contactName: '',
    password: '',
    confirmPassword: '',
    selectedTier: 'PROFESSIONAL',
    useCase: '',
    consentToTerms: false,
  });

  const [step, setStep] = useState<'info' | 'payment' | 'success'>('info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [squareConfig, setSquareConfig] = useState<SquareConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [successLicenseKey, setSuccessLicenseKey] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [cardContainerKey, setCardContainerKey] = useState(0); // Force remount when navigating back/forth
  const [tiers, setTiers] = useState<TierInfo[]>(FALLBACK_TIERS);
  const [tiersLoaded, setTiersLoaded] = useState(false);

  const cardRef = useRef<Card | null>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const isInitializingRef = useRef(false); // Prevent double initialization

  // Fetch tier pricing dynamically from Licensing Server
  useEffect(() => {
    if (!isOpen || tiersLoaded) return;

    const fetchTiers = async () => {
      try {
        const response = await fetch(`${licensingServerUrl}/api/public/tier-comparison/PDF Form Builder`);
        if (response.ok) {
          const data = await response.json();
          if (data.tiers) {
            // Single-Trial model: only the Professional tier is offered at trial signup
            const purchasable: TierInfo[] = data.tiers
              .filter((t: any) =>
                t.tier === 'PROFESSIONAL' &&
                t.pricing?.monthly?.priceCents > 0
              )
              .map((t: any) => ({
                value: t.tier,
                label: `${t.displayName || t.tier} - ${t.pricing.monthly.priceDisplay}`,
                price: t.pricing.monthly.priceDisplay,
                priceCents: t.pricing.monthly.priceCents,
              }));
            if (purchasable.length > 0) {
              setTiers(purchasable);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load tier pricing:', err);
      } finally {
        setTiersLoaded(true);
      }
    };
    fetchTiers();
  }, [isOpen, tiersLoaded, licensingServerUrl]);

  // Fetch Square configuration from Licensing Server
  useEffect(() => {
    if (!isOpen || squareConfig) return;

    const fetchSquareConfig = async () => {
      setConfigLoading(true);
      try {
        const response = await fetch(`${licensingServerUrl}/api/payment/config`);
        if (!response.ok) {
          throw new Error('Failed to fetch payment configuration');
        }
        const config: SquareConfig = await response.json();

        if (!config.applicationId || !config.locationId) {
          setError('Payment system is not configured. Please contact support.');
          return;
        }

        setSquareConfig(config);
      } catch (err) {
        console.error('Failed to fetch Square config:', err);
        setError('Unable to load payment system. Please try again later.');
      } finally {
        setConfigLoading(false);
      }
    };

    fetchSquareConfig();
  }, [isOpen, squareConfig, licensingServerUrl]);

  // Load Square SDK after config is fetched
  useEffect(() => {
    if (!isOpen || squareLoaded || !squareConfig) return;

    const loadSquareSDK = async () => {
      // Check if already loaded
      if (window.Square) {
        setSquareLoaded(true);
        return;
      }

      const script = document.createElement('script');
      // Use sandbox or production URL based on config
      script.src = squareConfig.sandbox
        ? 'https://sandbox.web.squarecdn.com/v1/square.js'
        : 'https://web.squarecdn.com/v1/square.js';
      script.async = true;
      script.onload = () => setSquareLoaded(true);
      script.onerror = () => setError('Failed to load payment system');
      document.body.appendChild(script);
    };

    loadSquareSDK();
  }, [isOpen, squareLoaded, squareConfig]);

  // Initialize Square card when on payment step
  useEffect(() => {
    if (step !== 'payment' || !squareLoaded || !window.Square || !squareConfig) return;
    if (isInitializingRef.current) return; // Prevent double initialization

    const initializeCard = async () => {
      isInitializingRef.current = true;

      try {
        // Ensure any previous card instance is destroyed
        if (cardRef.current) {
          try {
            await cardRef.current.destroy();
          } catch (e) {
            console.warn('Error destroying previous card:', e);
          }
          cardRef.current = null;
        }

        const payments = await window.Square!.payments(
          squareConfig.applicationId!,
          squareConfig.locationId!
        );
        const card = await payments.card();
        await card.attach('#card-container');
        cardRef.current = card;
        setCardReady(true);
      } catch (err) {
        console.error('Failed to initialize card:', err);
        setError('Failed to initialize payment form');
      } finally {
        isInitializingRef.current = false;
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initializeCard, 100);

    return () => {
      clearTimeout(timer);

      // Cleanup: destroy card and reset state
      const cleanup = async () => {
        if (cardRef.current) {
          try {
            await cardRef.current.destroy();
          } catch (e) {
            console.warn('Error during card cleanup:', e);
          }
          cardRef.current = null;
        }
        setCardReady(false);
        isInitializingRef.current = false;
      };

      cleanup();
    };
  }, [step, squareLoaded, squareConfig, cardContainerKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateInfoStep = (): boolean => {
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.companyName.trim()) {
      setError('Please enter your company name');
      return false;
    }
    if (!formData.contactName.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    setError(null);
    if (validateInfoStep()) {
      // Increment key to force fresh DOM element for Square card
      setCardContainerKey(prev => prev + 1);
      setStep('payment');
    }
  };

  const handleBackStep = () => {
    setError(null);
    setCardReady(false);
    setStep('info');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.consentToTerms) {
      setError('You must agree to the Terms of Service');
      return;
    }

    if (!cardRef.current) {
      setError('Payment form not ready. Please wait and try again.');
      return;
    }

    setLoading(true);

    try {
      // Get card nonce from Square
      const result = await cardRef.current.tokenize();

      if (result.status !== 'OK' || !result.token) {
        const errorMessage = result.errors?.[0]?.message || 'Failed to process card';
        setError(errorMessage);
        setLoading(false);
        return;
      }

      // Submit to Licensing Server
      const response = await fetch(`${licensingServerUrl}/api/payment/trial/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          companyName: formData.companyName,
          contactName: formData.contactName,
          password: formData.password,
          cardNonce: result.token,
          selectedTier: formData.selectedTier,
          consentToTerms: formData.consentToTerms,
          useCase: formData.useCase,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,  // User's browser timezone
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Registration failed');
        setLoading(false);
        return;
      }

      // Auto-apply the fresh license key so the app is licensed the moment
      // registration completes. Sign-in is NOT attempted here: trial accounts
      // are created email-unverified by design, and the Licensing Server
      // refuses login until the user clicks the verification link from the
      // trial welcome email. The success step and the Getting Started guide
      // walk the user through verify-then-sign-in.
      let licenseApplied = false;
      try {
        const applied = await applyLicenseKey(data.licenseKey);
        if (applied.success) {
          licenseApplied = true;
        } else {
          console.warn('Trial auto license-apply failed (non-fatal):', applied.error);
        }
      } catch (applyErr) {
        console.warn('Trial auto license-apply failed (non-fatal):', applyErr);
      }

      // Success! Show success step
      setSuccessLicenseKey(data.licenseKey);
      setStep('success');
      setLoading(false);

      // Tell the Getting Started guide the trial milestone was reached so it
      // can narrate the verification + sign-in path with the user's email.
      window.dispatchEvent(new CustomEvent('pfb-trial-registered', {
        detail: { email: formData.email, licenseApplied }
      }));
    } catch (err) {
      console.error('Registration error:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Re-send the verification email (LS endpoint is public; same CORS path as trial/register)
  const handleResendVerification = async () => {
    setResendStatus('sending');
    setResendMessage(null);
    try {
      const response = await fetch(`${licensingServerUrl}/api/customer/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await response.json().catch(() => ({} as { success?: boolean; message?: string }));
      if (response.ok && data.success !== false) {
        setResendStatus('sent');
        setResendMessage(data.message || `Verification email sent again to ${formData.email}.`);
      } else {
        setResendStatus('failed');
        setResendMessage(data.message || 'Could not resend right now. Please try again in a minute.');
      }
    } catch {
      setResendStatus('failed');
      setResendMessage('Could not reach the licensing server. Please try again in a minute.');
    }
  };

  // Handle closing from success step
  const handleSuccessClose = () => {
    if (successLicenseKey) {
      onSuccess(successLicenseKey);
    }
    handleClose();
  };

  const handleClose = () => {
    // Reset state
    setStep('info');
    setError(null);
    setSuccessLicenseKey(null);
    setResendStatus('idle');
    setResendMessage(null);
    setCardReady(false);
    setCardContainerKey(0);
    setFormData({
      email: '',
      companyName: '',
      contactName: '',
      password: '',
      confirmPassword: '',
      selectedTier: 'PROFESSIONAL',
      useCase: '',
      consentToTerms: false,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - dialog only closes via X button, not by clicking outside */}
      <div className="fixed inset-0 bg-black bg-opacity-50" />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              {step === 'success' ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900">One more step: verify your email</h2>
                  <p className="text-sm text-gray-500 mt-1">Your trial is created and your card is authorized. You can sign in as soon as your email is verified.</p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900">Start Your 7-Day Trial</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {step === 'info' ? 'Step 1 of 2: Your Information' : 'Step 2 of 2: Payment Details'}
                  </p>
                  {onEnterExistingKey && (
                    <p className="text-sm text-gray-600 mt-2">
                      Already have a license key?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          onEnterExistingKey();
                        }}
                        className="text-blue-600 hover:text-blue-700 font-medium underline"
                      >
                        Enter it here
                      </button>
                    </p>
                  )}
                </>
              )}
            </div>
            {step !== 'success' && (
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <div>{error}</div>
              {error.toLowerCase().includes('log in instead') && onEnterExistingKey && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onEnterExistingKey();
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-md font-medium text-white bg-blue-600 hover:bg-blue-700 text-sm"
                  >
                    Apply License Key
                  </button>
                </div>
              )}
            </div>
          )}

          {configLoading ? (
            /* Loading Config */
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading payment system...</span>
            </div>
          ) : step === 'info' ? (
            /* Step 1: User Information */
            <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formData.email.length > 0
                      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="you@company.com"
                  required
                />
                {formData.email.length > 0 && (
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? (
                    <p className="text-xs text-green-600 mt-1">✓ Valid email format</p>
                  ) : (
                    <p className="text-xs text-red-600 mt-1">✗ Please enter a valid email address</p>
                  )
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Acme Corp"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formData.password.length > 0
                        ? formData.password.length >= 8
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="Min. 8 characters"
                    required
                  />
                  {formData.password.length > 0 && formData.password.length < 8 && (
                    <p className="text-xs text-red-600 mt-1">
                      Password needs {8 - formData.password.length} more character{8 - formData.password.length !== 1 ? 's' : ''}
                    </p>
                  )}
                  {formData.password.length >= 8 && (
                    <p className="text-xs text-green-600 mt-1">✓ Password length OK</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formData.confirmPassword.length > 0
                        ? formData.password === formData.confirmPassword
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="Confirm password"
                    required
                  />
                  {formData.confirmPassword.length > 0 && (
                    formData.password === formData.confirmPassword ? (
                      <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                    ) : (
                      <p className="text-xs text-red-600 mt-1">✗ Passwords do not match</p>
                    )
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Your Plan *</label>
                <select
                  name="selectedTier"
                  value={formData.selectedTier}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {tiers.map(tier => (
                    <option key={tier.value} value={tier.value}>{tier.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Your card will be authorized but not charged until after the 7-day trial.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Use Case (Optional)</label>
                <textarea
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleInputChange}
                  maxLength={495}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="What will you use PDF Form Builder for?"
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {formData.useCase.length} / 495 characters
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={
                    !formData.email ||
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ||
                    !formData.contactName.trim() ||
                    !formData.companyName.trim() ||
                    formData.password.length < 8 ||
                    formData.confirmPassword.length < 8 ||
                    formData.password !== formData.confirmPassword
                  }
                  className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Payment
                </button>
              </div>
            </form>
          ) : (
            /* Step 2: Payment */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Order Summary</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium">{tiers.find(t => t.value === formData.selectedTier)?.label}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Trial Period:</span>
                  <span className="font-medium text-green-600">7 days FREE</span>
                </div>
                <div className="border-t mt-2 pt-2 flex justify-between">
                  <span className="text-gray-600">Charged after trial:</span>
                  <span className="font-bold text-lg">
                    {tiers.find(t => t.value === formData.selectedTier)?.price}
                  </span>
                </div>
              </div>

              {/* Square Card Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
                <div
                  key={cardContainerKey}
                  id="card-container"
                  ref={cardContainerRef}
                  className="border border-gray-300 rounded-lg p-3 min-h-[50px] bg-white"
                >
                  {!cardReady && (
                    <div className="text-gray-400 text-sm">Loading payment form...</div>
                  )}
                </div>
                {/*
                  The authorization is a ONE-TIME hold, so it states a bare
                  amount. This previously rendered the rate string ("$499/month")
                  and read as "authorized for $499/month" — which describes a
                  recurring authorization that does not exist, and is how a
                  single hold comes to look like a subscription on a statement.
                  The recurring figure belongs in the sentence's second half,
                  where it is genuinely a rate.
                */}
                <p className="text-xs text-gray-500 mt-2">
                  {(() => {
                    const tier = tiers.find(t => t.value === formData.selectedTier);
                    const once = tier?.priceCents ? formatUsd(tier.priceCents) : tier?.price;
                    return (
                      <>
                        Your card will be authorized for <strong>{once}</strong> today — a single hold,
                        not a charge. You won't be charged until your 7-day trial ends, then {tier?.price}.
                      </>
                    );
                  })()}
                </p>
              </div>

              {/* Terms of Service */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  name="consentToTerms"
                  id="consentToTerms"
                  checked={formData.consentToTerms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="consentToTerms" className="text-sm text-gray-600">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open('https://cavuinnovations.com/terms', '_blank', 'noopener,noreferrer');
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open('https://cavuinnovations.com/privacy', '_blank', 'noopener,noreferrer');
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBackStep}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !cardReady || !formData.consentToTerms}
                  className="flex-1 py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Start Free Trial'}
                </button>
              </div>

              <div className="text-center space-y-2">
                <p className="text-xs text-gray-500">
                  Cancel anytime during your trial via the self-service portal. No charge if canceled before trial ends.
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Close without registering
                </button>
              </div>
            </form>
          )}

          {/* Success Step */}
          {step === 'success' && successLicenseKey && (
            <div className="space-y-6">
              {/* Verification — the one thing the user must do before signing in */}
              <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-amber-100 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-bold text-amber-900">Check your email now</p>
                    <p className="text-amber-900 mt-1">
                      We sent a verification link to <strong className="break-all">{formData.email}</strong>.
                      <br />
                      <strong>Open that email and click the link.</strong> Until you do, signing in is refused
                      with &ldquo;Please verify your email address&rdquo;.
                    </p>
                    <ol className="mt-3 text-sm text-amber-900 list-decimal pl-5 space-y-1">
                      <li>Open the email from CAVU Innovations (check Spam / Junk if it isn&rsquo;t in your inbox within a minute)</li>
                      <li>Click <strong>Verify</strong> in the email</li>
                      <li>Come back here and click <strong>Sign In</strong> with your email, password and the license key below</li>
                    </ol>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendStatus === 'sending'}
                        className="px-3 py-1.5 rounded-md border border-amber-500 text-amber-900 font-medium hover:bg-amber-100 disabled:opacity-60"
                      >
                        {resendStatus === 'sending' ? 'Sending…' : 'Resend verification email'}
                      </button>
                      {resendMessage && (
                        <span className={resendStatus === 'failed' ? 'text-red-700' : 'text-green-800'}>{resendMessage}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 text-center text-sm">
                Your trial includes full access to all{' '}
                <strong>{tiers.find(t => t.value === formData.selectedTier)?.label.split(' - ')[0]}</strong> features for 7 days.
              </p>

              {/* License Key Display */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Your License Key:</p>
                <div className="bg-white border border-gray-300 rounded-lg p-3 font-mono text-sm break-all select-all">
                  {successLicenseKey}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Please save this key for your records. You'll need it if you reinstall the application.
                </p>
              </div>

              {/* Trial Info */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>First, verify your email</strong> via the link we just sent — sign-in is refused until you do</li>
                  <li>• Then sign in with your email, password and the license key above (it&rsquo;s already applied here)</li>
                  <li>• Your 7-day trial starts now</li>
                  <li>• Full access to {tiers.find(t => t.value === formData.selectedTier)?.label.split(' - ')[0]} tier features</li>
                  <li>• Your card will be charged {tiers.find(t => t.value === formData.selectedTier)?.price} after the trial ends</li>
                  <li>• Cancel anytime before the trial ends to avoid charges</li>
                </ul>
              </div>

              {/* Copy Button & Close */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(successLicenseKey);
                    // Could add a toast notification here
                  }}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy License Key
                </button>
                <button
                  type="button"
                  onClick={handleSuccessClose}
                  className="flex-1 py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-colors"
                >
                  Got it — I&rsquo;ll verify my email
                </button>
              </div>
            </div>
          )}

          {/* Sandbox Notice (for development) */}
          {squareConfig?.sandbox && step !== 'success' && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-xs">
              <strong>Sandbox Mode:</strong> Use test card 4111 1111 1111 1111, any future expiry, any CVV.
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default TrialRegistrationDialog;
